import { Ref } from 'react'
import { HighchartsReactRefObject, setHighcharts } from '@highcharts/react'
import { StockChart } from '@highcharts/react/Stock'
import { Box, CircularProgress, Grid, Typography, useTheme } from '@mui/material'
import Highcharts from 'highcharts/es-modules/masters/highcharts.src'

import 'highcharts/es-modules/masters/modules/stock.src'
import 'highcharts/es-modules/masters/modules/accessibility.src'
import 'highcharts/es-modules/masters/modules/annotations.src'
import 'highcharts/es-modules/masters/modules/exporting.src'
import 'highcharts/es-modules/masters/modules/offline-exporting.src'
import 'highcharts/es-modules/masters/modules/export-data.src'
import 'highcharts/es-modules/masters/modules/xrange.src'

import useSharedStyles from '../../../../utils/shared.styles'

export const dateTimeLabelFormats: Highcharts.AxisDateTimeLabelFormatsOptions = {
  day: { main: '%b %e' },
  hour: { main: '%b %e %Y %l:%M%P UTC' },
  minute: { main: '%b %e %Y %l:%M%P UTC' },
  second: { main: '%b %e %Y %l:%M%P UTC' },
  millisecond: { main: '%b %e %Y %l:%M%P UTC' },
}

export const tooltipDateTimeLabelFormats: Highcharts.TooltipDateTimeLabelFormatsOptions = {
  day: '%b %e',
  hour: '%b %e %Y %l:%M%P UTC',
  minute: '%b %e %Y %l:%M%P UTC',
  second: '%b %e %Y %l:%M%P UTC',
  millisecond: '%b %e %Y %l:%M%P UTC',
}

setHighcharts(Highcharts)

const ExtendHighcharts = (H: typeof Highcharts) => {
  // Add Legend Color
  H.wrap(H.Legend.prototype, 'colorizeItem', function colorizeItemWrapper(this: Highcharts.Chart, proceed, item, _visible, ...args) {
    const { color } = item

    // eslint-disable-next-line no-param-reassign
    if (item.options.legendColor) item.color = item.options.legendColor
    proceed.apply(this, args)
    // eslint-disable-next-line no-param-reassign
    item.color = color
  })
  // Limit CSV export to the selected date range
  H.wrap(H.Chart.prototype, 'getCSV', function limitDates(this: Highcharts.Chart, proceed) {
    const xAxis = this.xAxis[0]
    const { min, max } = xAxis.getExtremes()

    // Clone original series data for filtering
    const originalSeriesData = this.series.map((series) => ({
      series,
      originalData: [...((series.options as Highcharts.SeriesLineOptions).data as [number, number][])],
    }))

    // Filter series data to match the selected range
    this.series.forEach((series) => {
      if ((series.options as Highcharts.SeriesLineOptions).data) {
        const filteredData = ((series.options as Highcharts.SeriesLineOptions).data as [number, number][]).filter((point) => point[0] >= min && point[0] <= max)
        series.setData(filteredData, false) // Temporarily set filtered data
      }
    })

    // Generate the CSV with filtered data
    const csv = proceed.apply(this)

    // Restore the original data after CSV generation
    originalSeriesData.forEach(({ series, originalData }) => {
      series.setData(originalData, false)
    })

    return csv
  })
}

const BaseChart = ({
  chartData,
  chartRef,
  isLoading,
}: {
  chartData: Highcharts.Options
  chartRef: Ref<HighchartsReactRefObject> | undefined
  isLoading: boolean
}) => {
  const theme = useTheme()
  const sharedClasses = useSharedStyles

  const data = chartData?.series && chartData.series.length > 0 && (chartData.series[0] as Highcharts.SeriesLineOptions).data
  const dataPointCounts = data && data.length > 0 ? data.length : 0

  if (dataPointCounts > 0) {
    ExtendHighcharts(Highcharts)

    Highcharts.setOptions({
      chart: {
        animation: false,
        style: {
          fontFamily: "'Work Sans', sans-serif",
          fontSize: '16px',
          fontWeight: '400',
        },
      },
      credits: {
        enabled: false,
      },
      colors: ['#2D9CDB', '#E57C00', '#53D0C0', '#BF0E0E'],

      rangeSelector: {
        selected: 1,
        inputBoxWidth: 100,
        inputStyle: {
          color: theme.palette.primary.dark,
          padding: '12px 2px',
          borderRadius: '8px',
          border: `1px solid ${theme.palette.primary.dark}`,
        },
        buttonTheme: {
          fill: '#00000014',
          r: 12,
          states: {
            hover: {
              style: {
                color: 'black',
              },
            },
            select: {
              fill: theme.palette.primary.main,
              style: {
                color: 'white',
                fontWeight: 'normal',
              },
            },
          },
        },
      },
      xAxis: {
        type: 'datetime',
        crosshair: false,
        dateTimeLabelFormats: {
          ...dateTimeLabelFormats,
          hour: { main: '%l:%M%P' },
          minute: { main: '%l:%M%P' },
          second: { main: '%l:%M%P' },
        },
      },
      plotOptions: {
        series: {
          animation: false,
          tooltip: {
            valueDecimals: 1,
          },
        },
      },
      tooltip: {
        dateTimeLabelFormats: tooltipDateTimeLabelFormats,
        split: false,
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      yAxis: {
        opposite: false,
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'downloadPNG', 'downloadPDF', 'downloadCSV', 'viewData'],
          },
        },
      },
    })
    return <StockChart ref={chartRef} highcharts={Highcharts} options={chartData} />
  }
  return (
    <Box
      sx={[
        sharedClasses.centerContent,
        {
          display: 'flex',
          width: `${chartData?.chart?.width ?? 960}px`,
          height: `${chartData?.chart?.height ?? 500}px`,
          border: '1px solid #00000014',
        },
      ]}
    >
      <Grid container={true} sx={[sharedClasses.centerContentVertical, { flexDirection: 'column', gap: 3 }]}>
        {isLoading && <CircularProgress />}
        {isLoading ? (
          <Typography>Getting data for {chartData?.title?.text} chart</Typography>
        ) : (
          <Typography>No data available for {chartData?.title?.text} chart</Typography>
        )}
      </Grid>
    </Box>
  )
}

export default BaseChart
